'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import {
  Search, ArrowRight, Lock, Shield, Mail, FileText, Bug, Binary, Key, Globe, Cpu, FileSearch,
  FileJson, Code, Database, Clock, Image as ImageIcon, Terminal, GitBranch, Webhook, Wrench, Braces, Hash
} from 'lucide-react';
import type { Tool } from '@/lib/data/tools';

interface CategoryToolsGridProps {
  tools: Tool[];
  categorySlug: string;
}

// Extract slug from tool link
function getToolSlug(tool: Tool): string {
  return tool.link.replace('/tools/', '');
}

// Security tool subcategories
interface ToolSection {
  name: string;
  icon: React.ReactNode;
  description: string;
  slugPatterns: string[];
  keywords: string[];
}

// Developer tool subcategories
const DEVELOPER_SECTIONS: ToolSection[] = [
  {
    name: 'JSON & Data Format Tools',
    icon: <FileJson className="w-5 h-5" />,
    description: 'JSON formatting, validation, and data format conversion utilities',
    slugPatterns: ['json', 'yaml', 'csv', 'excel', 'data-format', 'xml'],
    keywords: ['json', 'yaml', 'csv', 'excel', 'xml', 'toml', 'format', 'convert'],
  },
  {
    name: 'Code & Text Tools',
    icon: <Code className="w-5 h-5" />,
    description: 'Regex testing, diff checking, markdown preview, encoding, and text analysis',
    slugPatterns: ['regex', 'diff', 'markdown', 'word-counter', 'html-encoder', 'base64', 'hash-generator'],
    keywords: ['regex', 'diff', 'markdown', 'word', 'text', 'html', 'encode', 'decode', 'base64', 'hex', 'binary', 'hash', 'md5', 'sha'],
  },
  {
    name: 'API & Web Development',
    icon: <Webhook className="w-5 h-5" />,
    description: 'HTTP requests, webhooks, authentication, and web debugging tools',
    slugPatterns: ['http', 'webhook', 'url-encoder', 'user-agent', 'rate-limit', 'status-code', 'jwt', 'oauth', 'csp', 'cors'],
    keywords: ['http', 'webhook', 'url', 'api', 'request', 'user-agent', 'rate', 'jwt', 'token', 'oauth', 'oidc', 'openid', 'csp', 'cors', 'content security policy', 'cross-origin'],
  },
  {
    name: 'Database & SQL Tools',
    icon: <Database className="w-5 h-5" />,
    description: 'SQL formatting, dialect translation, and database utilities',
    slugPatterns: ['sql'],
    keywords: ['sql', 'database', 'query', 'mysql', 'postgres'],
  },
  {
    name: 'DevOps & Infrastructure',
    icon: <Terminal className="w-5 h-5" />,
    description: 'Docker, Terraform, Kubernetes, AWS, and infrastructure automation tools',
    slugPatterns: ['docker', 'terraform', 'aws-s3', 'dockerfile', 'kubernetes', 'sbom'],
    keywords: ['docker', 'terraform', 'aws', 'container', 'infrastructure', 'devops', 'kubernetes', 'k8s', 'manifest', 'sbom', 'software bill of materials', 'supply chain'],
  },
  {
    name: 'Git & Version Control',
    icon: <GitBranch className="w-5 h-5" />,
    description: 'Git commands, .gitignore generation, and semantic versioning',
    slugPatterns: ['git', 'semver'],
    keywords: ['git', 'version', 'semver', 'gitignore'],
  },
  {
    name: 'Generator & Mock Data Tools',
    icon: <Braces className="w-5 h-5" />,
    description: 'UUID generation, mock data, Lorem ipsum, and test data creation',
    slugPatterns: ['uuid', 'mock-data', 'lorem', 'qr-code', 'ascii-art', 'color-picker'],
    keywords: ['uuid', 'guid', 'mock', 'lorem', 'placeholder', 'generate', 'qr', 'ascii', 'color'],
  },
  {
    name: 'Time & Scheduling Tools',
    icon: <Clock className="w-5 h-5" />,
    description: 'Unix timestamps, cron expressions, and timezone utilities',
    slugPatterns: ['timestamp', 'cron', 'world-clock'],
    keywords: ['timestamp', 'unix', 'cron', 'time', 'timezone', 'clock', 'schedule'],
  },
  {
    name: 'Image & Media Tools',
    icon: <ImageIcon className="w-5 h-5" />,
    description: 'Image format conversion and media processing utilities',
    slugPatterns: ['webp', 'heic', 'image', 'pdf-converter'],
    keywords: ['image', 'webp', 'heic', 'gif', 'jpg', 'png', 'pdf', 'convert'],
  },
  {
    name: 'PowerShell & Windows',
    icon: <Terminal className="w-5 h-5" />,
    description: 'PowerShell command generators for Exchange, Azure AD, and Windows',
    slugPatterns: ['powershell', 'exchange-online', 'azure-ad', 'windows-update'],
    keywords: ['powershell', 'exchange', 'azure', 'windows', 'microsoft'],
  },
  {
    name: 'Project Config Generators',
    icon: <Wrench className="w-5 h-5" />,
    description: 'Generate configuration files for Python, Node.js, Docker, and more',
    slugPatterns: ['requirements', 'package-json', 'env-file', 'dockerfile-generator'],
    keywords: ['requirements', 'package.json', 'env', 'config', 'setup'],
  },
  {
    name: 'AI & LLM Tools',
    icon: <Hash className="w-5 h-5" />,
    description: 'Token counting, prompt optimization, and AI development utilities',
    slugPatterns: ['llm', 'token'],
    keywords: ['llm', 'token', 'gpt', 'claude', 'ai', 'prompt'],
  },
  {
    name: 'File & Utility Tools',
    icon: <FileText className="w-5 h-5" />,
    description: 'File analysis, metadata extraction, and general utilities',
    slugPatterns: ['file-metadata', 'book-page', 'tld', 'sri-hash'],
    keywords: ['file', 'metadata', 'book', 'tld', 'domain', 'sri'],
  },
];

const SECURITY_SECTIONS: ToolSection[] = [
  {
    name: 'Cipher & Encryption Tools',
    icon: <Lock className="w-5 h-5" />,
    description: 'Classical ciphers, modern encryption, and cryptanalysis utilities',
    slugPatterns: ['cipher', 'rot13', 'xor', 'aes-encryption', 'one-time-pad', 'encoding-chain'],
    keywords: ['cipher', 'encryption', 'encrypt', 'decrypt', 'encode', 'decode', 'rot13', 'xor', 'aes'],
  },
  {
    name: 'Certificate & SSL Tools',
    icon: <Shield className="w-5 h-5" />,
    description: 'X.509 certificates, SSL/TLS analysis, and PKI utilities',
    slugPatterns: ['x509', 'ssl', 'certificate', 'csr', 'chain-builder', 'ocsp', 'ct-lookup', 'transparency'],
    keywords: ['certificate', 'ssl', 'tls', 'x509', 'csr', 'pem', 'chain', 'ocsp', 'crl'],
  },
  {
    name: 'Key Generation Tools',
    icon: <Key className="w-5 h-5" />,
    description: 'Generate SSH keys, PGP keys, and other cryptographic key pairs',
    slugPatterns: ['ssh-key', 'pgp-key', 'key-generator'],
    keywords: ['ssh', 'pgp', 'gpg', 'key', 'keypair', 'ed25519', 'rsa'],
  },
  {
    name: 'Email Security Tools',
    icon: <Mail className="w-5 h-5" />,
    description: 'SPF, DKIM, DMARC configuration and email header analysis',
    slugPatterns: ['email', 'spf', 'dkim', 'dmarc', 'mx-'],
    keywords: ['email', 'spf', 'dkim', 'dmarc', 'mail', 'smtp', 'header'],
  },
  {
    name: 'Vulnerability & Threat Intel',
    icon: <Bug className="w-5 h-5" />,
    description: 'CVE lookup, MITRE ATT&CK, threat intelligence, and vulnerability analysis',
    slugPatterns: ['cve', 'cwe', 'cvss', 'mitre', 'threat-intel', 'ioc', 'ip-risk', 'domain-spoofing', 'whois', 'geolocation'],
    keywords: ['cve', 'cwe', 'cvss', 'mitre', 'attack', 'threat', 'vulnerability', 'ioc', 'indicator', 'domain', 'whois', 'registration', 'nameserver', 'geolocation', 'isp', 'reputation'],
  },
  {
    name: 'Malware Analysis Tools',
    icon: <FileSearch className="w-5 h-5" />,
    description: 'Binary analysis, string extraction, disassembly, and malware forensics',
    slugPatterns: ['malware', 'deobfuscator', 'string-extractor', 'entropy', 'hex-editor', 'disassembler', 'magic-number'],
    keywords: ['malware', 'binary', 'strings', 'entropy', 'hex', 'deobfuscate', 'disassemble'],
  },
  {
    name: 'Hash & Encoding Tools',
    icon: <Binary className="w-5 h-5" />,
    description: 'Hash generation, Base64 encoding, and data format utilities',
    slugPatterns: ['hash', 'base64', 'jwt', 'url-defanger'],
    keywords: ['hash', 'md5', 'sha', 'base64', 'jwt', 'token'],
  },
  {
    name: 'Web Security Tools',
    icon: <Globe className="w-5 h-5" />,
    description: 'Security headers, CSP policies, CORS analysis, and web security testing',
    slugPatterns: ['security-headers', 'csp', 'cors', 'cookie', 'robots', 'secrets-scanner', 'url-expander', 'redirect-chain', 'http-request', 'sri-hash'],
    keywords: ['header', 'csp', 'cors', 'cookie', 'security', 'web', 'redirect', 'shorten', 'http request', 'sri', 'subresource', 'integrity'],
  },
  {
    name: 'Security Operations',
    icon: <Cpu className="w-5 h-5" />,
    description: 'SIEM queries, incident response, compliance mapping, and security operations',
    slugPatterns: ['siem', 'incident', 'nist', 'sbom', 'kubernetes', 'nmap', 'metasploit', 'ransomware', 'playbook', 'regex-tester'],
    keywords: ['siem', 'splunk', 'incident', 'nist', 'compliance', 'kubernetes', 'nmap', 'pentest', 'regex', 'regular expression', 'pattern library'],
  },
  {
    name: 'Password & Authentication',
    icon: <FileText className="w-5 h-5" />,
    description: 'Password strength testing, generation, and authentication tools',
    slugPatterns: ['password', 'oauth', 'oidc'],
    keywords: ['password', 'auth', 'oauth', 'oidc', 'login'],
  },
];

const NETWORK_SECTIONS: ToolSection[] = [
  {
    name: 'DNS & Domain Tools',
    icon: <Globe className="w-5 h-5" />,
    description: 'DNS record lookup, domain registration, and nameserver analysis',
    slugPatterns: ['dns', 'whois'],
    keywords: ['dns', 'domain', 'whois', 'nameserver', 'mx', 'txt', 'registration'],
  },
  {
    name: 'IP & Address Tools',
    icon: <Globe className="w-5 h-5" />,
    description: 'IP geolocation, address conversion, subnet calculation, and BGP analysis',
    slugPatterns: ['ip-', 'ipv4', 'ipv6', 'subnet', 'bgp', 'cidr'],
    keywords: ['ip', 'ipv4', 'ipv6', 'subnet', 'cidr', 'bgp', 'asn', 'geolocation', 'address'],
  },
  {
    name: 'URL Analysis Tools',
    icon: <Globe className="w-5 h-5" />,
    description: 'URL expansion, redirect chain tracing, and link analysis',
    slugPatterns: ['url-', 'redirect'],
    keywords: ['url', 'redirect', 'shorten', 'expand', 'link'],
  },
  {
    name: 'Network Performance Tools',
    icon: <Cpu className="w-5 h-5" />,
    description: 'Latency calculation, TCP optimization, and bandwidth analysis',
    slugPatterns: ['latency', 'tcp-window', 'bandwidth'],
    keywords: ['latency', 'tcp', 'window', 'bandwidth', 'throughput', 'ping', 'rtt'],
  },
  {
    name: 'Infrastructure & Reference',
    icon: <Terminal className="w-5 h-5" />,
    description: 'MAC address lookup, port reference, firewall rules, and wireless planning',
    slugPatterns: ['mac-', 'port-', 'firewall', 'wireless'],
    keywords: ['mac', 'port', 'firewall', 'wireless', 'wifi', '802.11', 'oui', 'vendor'],
  },
];

// Categorize a tool into a section based on category
function categorizeTool(tool: Tool, sections: ToolSection[]): string | null {
  const slug = getToolSlug(tool).toLowerCase();
  const name = tool.name.toLowerCase();
  const desc = tool.description.toLowerCase();

  for (const section of sections) {
    // Check slug patterns
    for (const pattern of section.slugPatterns) {
      if (slug.includes(pattern)) {
        return section.name;
      }
    }
    // Check keywords in name and description
    for (const keyword of section.keywords) {
      if (name.includes(keyword) || desc.includes(keyword)) {
        return section.name;
      }
    }
  }

  return null; // Uncategorized
}

// Get sections for a category
function getSectionsForCategory(categorySlug: string): ToolSection[] | null {
  if (categorySlug === 'security') return SECURITY_SECTIONS;
  if (categorySlug === 'developer') return DEVELOPER_SECTIONS;
  if (categorySlug === 'network') return NETWORK_SECTIONS;
  return null;
}

// Get fallback section for uncategorized tools
function getFallbackSection(categorySlug: string): ToolSection {
  if (categorySlug === 'security') {
    return {
      name: 'Other Security Tools',
      icon: <Shield className="w-5 h-5" />,
      description: 'Additional security utilities',
      slugPatterns: [],
      keywords: [],
    };
  }
  if (categorySlug === 'network') {
    return {
      name: 'Other Network Tools',
      icon: <Globe className="w-5 h-5" />,
      description: 'Additional network utilities',
      slugPatterns: [],
      keywords: [],
    };
  }
  return {
    name: 'Other Developer Tools',
    icon: <Code className="w-5 h-5" />,
    description: 'Additional developer utilities',
    slugPatterns: [],
    keywords: [],
  };
}

// Tool Card Component
function ToolCard({ tool }: { tool: Tool }) {
  if (tool.comingSoon) {
    return (
      <div className="p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 h-full relative overflow-hidden">
        <div className="absolute top-4 right-4">
          <span className="inline-block bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
            Coming Soon
          </span>
        </div>
        <div className="flex items-start gap-3 mb-4">
          <div className="text-4xl flex-shrink-0 opacity-60">{tool.icon}</div>
          <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 leading-tight pt-1">
            {tool.name}
          </h3>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {tool.description}
        </p>
      </div>
    );
  }

  return (
    <Link
      href={`/tools/${getToolSlug(tool)}`}
      className="p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-[#2813e8] bg-white dark:bg-gray-800 hover:shadow-2xl transition-all duration-300 block h-full relative overflow-hidden group"
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2813e8]/5 to-[#2cc3d1]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="relative z-10">
        <div className="flex items-start gap-3 mb-4">
          <div className="text-4xl flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300">
            {tool.icon}
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-[#2813e8] transition-colors leading-tight pt-1">
            {tool.name}
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
          {tool.description}
        </p>
        <div className="flex items-center gap-2 text-[#2813e8] font-semibold text-sm">
          <span>Try it now</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
        </div>
      </div>
    </Link>
  );
}

// Generate a URL-friendly ID from section name
function getSectionId(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

export default function CategoryToolsGrid({ tools, categorySlug }: CategoryToolsGridProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTools = tools.filter(tool => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      tool.name.toLowerCase().includes(query) ||
      tool.description.toLowerCase().includes(query)
    );
  });

  // Group tools by section for security and developer categories
  const groupedTools = useMemo(() => {
    const sections = getSectionsForCategory(categorySlug);

    if (!sections || searchQuery.trim()) {
      return null; // Don't group when searching or for categories without sections
    }

    const groups: Record<string, Tool[]> = {};
    const uncategorized: Tool[] = [];

    for (const tool of filteredTools) {
      const sectionName = categorizeTool(tool, sections);
      if (sectionName) {
        if (!groups[sectionName]) {
          groups[sectionName] = [];
        }
        groups[sectionName].push(tool);
      } else {
        uncategorized.push(tool);
      }
    }

    // Sort sections by the order defined in the sections array
    const orderedGroups: { section: ToolSection; tools: Tool[] }[] = [];
    for (const section of sections) {
      if (groups[section.name] && groups[section.name].length > 0) {
        orderedGroups.push({ section, tools: groups[section.name] });
      }
    }

    // Add uncategorized at the end if there are any
    if (uncategorized.length > 0) {
      orderedGroups.push({
        section: getFallbackSection(categorySlug),
        tools: uncategorized,
      });
    }

    return orderedGroups;
  }, [filteredTools, categorySlug, searchQuery]);

  // Handle hash anchor scrolling after content is rendered
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      // Small delay to ensure content is rendered
      const timeoutId = setTimeout(() => {
        const hash = window.location.hash.substring(1);
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [groupedTools]);

  return (
    <>
      {/* Search Bar */}
      <div className="mb-8">
        <div className="relative max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search tools..."
            aria-label="Search tools in this category"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-12 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:border-[#2813e8] focus:ring-2 focus:ring-[#2813e8]/20 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {filteredTools.length} {filteredTools.length === 1 ? 'tool' : 'tools'} found
          </p>
        )}
      </div>

      {/* Grouped Tools for Security Category */}
      {groupedTools && !searchQuery.trim() ? (
        <div className="space-y-12">
          {groupedTools.map(({ section, tools: sectionTools }) => (
            <div key={section.name} id={getSectionId(section.name)} className="scroll-mt-24">
              {/* Section Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] text-white rounded-lg">
                  {section.icon}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{section.name}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{section.description}</p>
                </div>
                <span className="ml-auto text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                  {sectionTools.length} {sectionTools.length === 1 ? 'tool' : 'tools'}
                </span>
              </div>

              {/* Section Tools Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sectionTools.map((tool) => (
                  <div key={tool.name} className="relative group">
                    <ToolCard tool={tool} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Flat Grid for other categories or when searching */
        filteredTools.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTools.map((tool) => (
              <div key={tool.name} className="relative group">
                <ToolCard tool={tool} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">No tools found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              Try adjusting your search terms
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-[#2813e8] font-semibold hover:underline"
            >
              Clear search
            </button>
          </div>
        )
      )}
    </>
  );
}
